import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash';

import { MODES } from 'constants';
import mediator, { ACTIONS } from 'mediator';

import API from 'lib/tessitura/API';
import logger from 'lib/common/logger';
import fetcher from 'lib/common/fetcher';
import features from 'lib/common/features';
import arrayWrap from 'lib/common/arrayWrap';
import packages from 'lib/cart/packages';
import panToElement from 'lib/seatmaps/panToElement';
import preselectLevel from 'lib/seatmaps/preselectLevel';
import preselectScreen from 'lib/seatmaps/preselectScreen';
import generateSeatMap from 'lib/seatmaps/generateSeatMap';
import combineSubLineItems from 'lib/cart/combineSubLineItems';
import fetchSeatViews from 'lib/seats/fetchSeatViews';
import { redirectWithError } from 'lib/common/fallbacks';

/**
 * Higher order components
 */
import withCondition from 'hocs/withCondition';

/**
 * Container components
 */
import SeatMapContainer from 'containers/SeatMapContainer';
import DetailsContainer from 'containers/DetailsContainer';
import PanelContainer from 'containers/PanelContainer';

/**
 * Presentational Components
 */
import Details from 'components/Details';
import BestAvailableLink from 'components/BestAvailableLink';
import PromoCode from 'components/PromoCode';
import PerformanceSelector from 'components/PerformanceSelector';
import LevelSelector from 'components/LevelSelector';
import ScreenSelector from 'components/ScreenSelector';
import PriceKey from 'components/PriceKey';
import SeatKey from 'components/SeatKey';
import MiniMap from 'components/MiniMap';
import ExpirationTimer from 'components/ExpirationTimer';

const PanelDetails = withCondition(Details);
const PanelBestAvailableLink = withCondition(BestAvailableLink);
const PanelPromoCode = withCondition(PromoCode);
const PanelPerformanceSelector = withCondition(PerformanceSelector);
const PanelLevelSelector = withCondition(LevelSelector);
const PanelScreenSelector = withCondition(ScreenSelector);
const PanelPriceKey = withCondition(PriceKey);
const PanelSeatKey = withCondition(SeatKey);
const PanelMiniMap = withCondition(MiniMap);

class SYOSContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentPerformanceId: props.initialPerformanceId,
      currentCart: null,
      currentCartItems: null,
      currentFixedPackage: null,
      currentFlexPackage: null,
      currentFlexPackagePerformance: null,
      isFixedPackagePerformance: false,
      svgData: null,
      levels: [],
      zonesByLevel: null,
      seatViews: null,
      isEnhanced: false,
      levelSelected: false,
      selectedScreenId: '1',
      panZoomInstance: null,
    };

    this.onChangePerformance = this.onChangePerformance.bind(this);
    this.fetchSeatMap = this.fetchSeatMap.bind(this);
    this.fetchSeatViews = this.fetchSeatViews.bind(this);
    this.onScreenSelected = this.onScreenSelected.bind(this);
    this.onSeatMapReady = this.onSeatMapReady.bind(this);
    this.onPanZoomInit = this.onPanZoomInit.bind(this);
  }

  componentDidMount() {
    const { isEnhanced } = this.state;
    const { loginInfo, usesGeneratedSeatmap, seatData } = this.props;

    if (!isEnhanced && usesGeneratedSeatmap && seatData.length) {
      let svgData;

      try {
        svgData = generateSeatMap(seatData);
      } catch (e) {
        redirectWithError(new Error('Failed to create seat-map'));
      }

      this.setState({
        svgData: svgData,
      });
    }

    this.fetchSeatMap(loginInfo).then(() => {
      this.fetchSeatViews();
    });
  }

  onChangePerformance(performanceId) {
    let cartDetails = {};
    mediator.publish(ACTIONS.UPDATE_CART_DETAILS, cartDetails);

    if (cartDetails.count && !window.confirm('Changing performance will clear previously selected seats.\nDo you wish to continue?')) {
      return false;
    }

    this.props.onChangePerformance(performanceId);

    // Store a reference to previous panZoom state
    let oldPanZoom = this.state.panZoomInstance;

    if (!oldPanZoom) {
      return;
    }

    let currentPan = oldPanZoom.getPan();
    let currentZoom = oldPanZoom.getZoom();

    /**
     * Reset only the state that needs to
     * change when switching performance.
     */
    this.setState({
      currentPerformanceId: performanceId,
      currentCart: null,
      currentCartItems: null,
      svgData: null,
      zonesByLevel: null,
      seatViews: null,
      isEnhanced: false,
      selectedScreenId: null,
      panZoomInstance: null
    });

    /**
     * Re-fetch seat-map.
     *
     * As we modify the seat-map DOM the simplest way to
     * reset it is to completely re-render the seat-map.
     *
     * This is quick so it preferable to the complex
     * state-management needed to reset the existing seat-map.
     */
    this.fetchSeatMap(this.props.loginInfo).then(() => {
      this.fetchSeatViews();

      // Reset pan position and zoom level.
      let newPanZoom = this.state.panZoomInstance;

      if (newPanZoom) {
        newPanZoom.zoom(currentZoom);
        newPanZoom.pan(currentPan);
      }
    });
  }

  fetchSeatMap() {
    if (this.props.currentFacility) {
      return fetcher.fetchHtml(this.props.currentFacility.svg).then(svgData => {
        this.setState({
          svgData: svgData,
          levelSelected: true,
          seatMapReady: true
        });

        return svgData;
      });
    } else {
      return Promise.resolve(null);
    }
  }

  fetchSeatViews() {
    let endpoint = features.getOption('viewFromSeat', 'endpoint');
    if (endpoint) {
      fetchSeatViews(endpoint, this.props.currentFacility).then(seatViews => {
        if (seatViews) {
          this.setState({ seatViews: seatViews });
        }
      });
    }
  }

  onScreenSelected(screenId) {
    this.setState({
      levelSelected: !this.state.levelSelected ? true : false,
      selectedScreenId: screenId
    });

    mediator.publish(ACTIONS.SCREEN_FILTER_SELECTED, {
      selected: screenId
    });
  }

  onSeatMapReady(collectedSeats, zonesByLevel, levels) {
    const perfNo = this.state.currentPerformanceId;
    const packageNo = this.props.packageNo;
    const mode = this.props.mode;

    let states = {
      isEnhanced: true
    };

    if (zonesByLevel) {
      states.zonesByLevel = zonesByLevel;
    }
    if (levels) {
      states.levels = levels;
    }

    this.setState(states);

    /**
     * Fetch existing cart
     * TODO: Could this happen earlier?
     */
    API.getCart().then(currentCart => {
      const cart = currentCart || null;

      const cartItems = cart ? combineSubLineItems(mode, cart, perfNo, packageNo) : [];

      const packageItems = arrayWrap(get(cart, 'PackageLineItem'));

      let currentFixedPackage = null;
      let currentFlexPackage = null;
      let currentFlexPackagePerformance = null;
      let isFixedPackagePerformance = false;

      if (packageItems.length && packageNo) {
        currentFixedPackage = packages.getCurrentFixedPackage(packageItems, packageNo);
      }

      if (packageItems.length && packageNo) {
        currentFlexPackage = packages.getCurrentFlexPackage(packageItems, packageNo);
      }

      if (packageItems.length && packageNo && perfNo) {
        currentFlexPackagePerformance = packages.getCurrentFlexPackagePerformance(packageItems, packageNo, perfNo);
      }

      if (packageItems.length) {
        isFixedPackagePerformance = !!packages.getCurrentFixedPackagePerformance(packageItems);
      }

      this.setState({
        currentCart: cart,
        currentCartItems: cartItems,
        currentFixedPackage: currentFixedPackage,
        currentFlexPackage: currentFlexPackage,
        currentFlexPackagePerformance: currentFlexPackagePerformance,
        isFixedPackagePerformance: isFixedPackagePerformance,
      });

      if (this.props.usesGeneratedSeatmap && this.props.screenZoneData) {
        const selectedScreenNo = preselectScreen(cartItems, this.props.screenZoneData);

        if (selectedScreenNo) {
          this.onScreenSelected(selectedScreenNo);
        }

        this.setState({
          seatMapReady: true,
        });
      } else {
        preselectLevel(cartItems, this.props.seatData);
      }
    }, error => {
      console.log(error);
      logger.captureMessage('Failed to get cart');

      this.setState({
        currentCart: false,
        cartItems: [],
      });
    });
  }

  onPanZoomInit(panZoomInstance) {
    this.setState({
      panZoomInstance: panZoomInstance
    });

    let selectedLevel = this.props.selectedLevel;
    if (selectedLevel && features.isEnabled('levelSelector')) {
      const selectedLevelEl = document.getElementById(selectedLevel.selector);
      if (selectedLevelEl) {
        panToElement(selectedLevelEl, panZoomInstance, selectedLevel.newZoom);
      }
    }
  }

  render() {
    const { mode } = this.props;

    const shouldRenderMiniMap =
      features.isEnabled('miniMap') &&
      !this.props.usesGeneratedSeatmap &&
      this.state.panZoomInstance &&
      (window.matchMedia && window.matchMedia('(min-width: 30em)').matches);

    return (
      <div
        className={classnames(
          'syos',
          `syos--facility-${get(
            this.props.currentFacility,
            'facility_no',
            'unknown'
          )}`
        )}
      >
        <div className="syos__body">
          <div className="syos__canvas">
            {this.state.svgData ? (
              <SeatMapContainer
                usesGeneratedSeatmap={this.props.usesGeneratedSeatmap}
                seatMapReady={this.state.seatMapReady}
                svgData={this.state.svgData}
                seatData={this.props.seatData}
                isEnhanced={this.state.isEnhanced}
                selectedLevel={this.props.selectedLevel}
                onPanZoomInit={this.onPanZoomInit}
                onSeatMapReady={this.onSeatMapReady}
                colours={this.props.colours}
                returnUrl={this.props.returnUrl}
                prices={this.props.prices}
                additionalPrices={this.props.additionalPrices}
              />
            ) : null}

            {this.state.currentCart && features.isEnabled('expirationTimer') ? (
              <ExpirationTimer cart={this.state.currentCart} />
            ) : null}

            {!!this.props.prices.length &&
            !!this.props.seatData.length &&
            this.state.currentCartItems !== null ? (
              <DetailsContainer
                mode={mode}
                onComplete={this.props.onComplete}
                prices={this.props.prices}
                additionalPrices={this.props.additionalPrices}
                seatData={this.props.seatData}
                seatViews={this.state.seatViews}
                priceTypeOptions={this.props.priceTypeOptions}
                currentCart={this.state.currentCart}
                currentCartItems={this.state.currentCartItems}
                currentFixedPackage={this.state.currentFixedPackage}
                currentFlexPackage={this.state.currentFlexPackage}
                currentFlexPackagePerformance={this.state.currentFlexPackagePerformance}
                isFixedPackagePerformance={this.state.isFixedPackagePerformance}
                performances={this.props.performances}
                currentPerformanceId={this.props.performanceDetails.Performance.inv_no}
                currentPackageNo={this.props.packageNo}
                performanceGroupNo={this.props.performanceGroupNo}
                loginInfo={this.props.loginInfo}
                activePromoCode={this.props.activePromoCode}
                mainPanZoomInstance={this.state.panZoomInstance}
                onChangePerformance={this.onChangePerformance}
                performanceDetails={this.props.performanceDetails}
                priceTypes={this.props.priceTypes}
                additionalPriceTypes={this.props.additionalPriceTypes}
                currentFacility={this.props.currentFacility}
              />
            ) : null}

            <PanelContainer>
              <PanelDetails
                performanceDetails={this.props.performanceDetails}
                isAvailable={features.isEnabled('details')}
              />
              <PanelBestAvailableLink
                currentPerformanceId={this.state.currentPerformanceId}
                label="Choose Best Available Seats"
                isAvailable={!!this.state.currentPerformanceId}
              />
              <PanelPromoCode
                onPromoCodeReady={this.props.onPromoCodeReady}
                isAvailable={features.isEnabled('promoCode')}
              />
              <PanelPerformanceSelector
                performances={this.props.performances}
                currentPerformanceId={this.props.performanceDetails.Performance.inv_no}
                onChangePerformance={this.onChangePerformance}
                isAvailable={
                  features.isEnabled('performanceSelector') && (mode === MODES.PERFORMANCE || mode === MODES.FLEX_PACKAGE_PERFORMANCE)
                }
              />
              <PanelLevelSelector
                levels={this.state.levels}
                selectedLevel={this.props.selectedLevel}
                isAvailable={
                  !this.props.usesGeneratedSeatmap &&
                  this.state.levels.length > 1
                }
                performanceId={this.props.initialPerformanceId}
              />
              <PanelScreenSelector
                screens={this.props.screenData}
                screenZoneData={this.props.screenZoneData}
                isAvailable={
                  this.props.usesGeneratedSeatmap && !!this.props.screenZoneData
                }
                selectedScreenId={this.state.selectedScreenId}
                onScreenSelected={this.onScreenSelected}
              />
              <PanelMiniMap
                mainPanZoomInstance={this.state.panZoomInstance}
                currentFacility={this.props.currentFacility}
                isAvailable={shouldRenderMiniMap}
              />
              <PanelPriceKey
                prices={this.props.prices}
                screenZoneData={this.props.screenZoneData}
                zonesByLevel={this.state.zonesByLevel}
                isAvailable={
                  features.isEnabled('priceFilters') &&
                  !!this.props.prices.length
                }
                colours={this.props.colours}
              />
              <PanelSeatKey
                currentFacility={this.props.currentFacility}
                isAvailable={
                  features.isEnabled('seatKey') && this.state.isEnhanced
                }
              />
            </PanelContainer>
          </div>
        </div>
      </div>
    );
  }
}

SYOSContainer.propTypes = {
  initialPerformanceId: PropTypes.number.isRequired,
  packageNo: PropTypes.number,
  performanceGroupNo: PropTypes.number,
  loginInfo: PropTypes.object.isRequired,
  performanceDetails: PropTypes.object,
  performances: PropTypes.array,
  prices: PropTypes.array,
  additionalPrices: PropTypes.array,
  seatData: PropTypes.array,
  screenData: PropTypes.array,
  screenZoneData: PropTypes.object,
  selectedLevel: PropTypes.object,
  currentFacility: PropTypes.object,
  usesGeneratedSeatmap: PropTypes.bool,
  activePromoCode: PropTypes.string,
  onChangePerformance: PropTypes.func.isRequired,
  onPromoCodeReady: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  priceTypes: PropTypes.array,
  additionalPriceTypes: PropTypes.array,
  colours: PropTypes.array,
  mode: PropTypes.string,
  priceTypeOptions: PropTypes.object,
  returnUrl: PropTypes.string,
};

export default SYOSContainer;



// WEBPACK FOOTER //
// ./src/javascripts/containers/SYOSContainer.js